import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ImageOff, Search } from 'lucide-react';
import type { Recipe, RecipeComponent } from '../../../services/recipes/recipesService';
import { fetchNotesByRecipeId, RecipeNote } from '../../../services/recipeNotes/recipeNotesService';
import { useRecipeViewer } from './RecipeViewerContext';
import { ViewerBack, ViewerLink, useViewerScroll } from './viewerNavigation';
import { ViewerDeclaration } from './RecipeViewerAllergies';
import { RecordedAllergens } from '../../recipes/RecipeAllergenSummary';
import { componentRouteKey, filterViewerRecipes, orderedRecipeRows, recipeName, resolveViewerComponent,
  translatedText, VIEWER_SORTS, ViewerService, ViewerSort } from './recipeViewerRecipeUtils';
import './recipeViewerRecipes.css';

export function RecipeViewerGrid() {
  const { recipes, restaurantId } = useRecipeViewer();
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  useViewerScroll();
  const query = params.get('q') ?? '';
  const category = params.get('category') ?? '';
  const requestedService = params.get('service');
  const service: ViewerService = requestedService === 'FOOD' || requestedService === 'DRINKS' ? requestedService : 'ALL';
  const requestedSort = params.get('sort');
  const sort: ViewerSort = VIEWER_SORTS.includes(requestedSort as ViewerSort) ? requestedSort as ViewerSort : 'menu-order';
  const scopedRecipes = useMemo(() => recipes.filter(recipe => recipe.restaurantId === restaurantId), [recipes, restaurantId]);
  const serviceSupported = scopedRecipes.some(recipe => Array.isArray(recipe.serviceTypes));
  const categories = useMemo(() => Array.from(new Map(scopedRecipes.filter(recipe => recipe.category).map(recipe =>
    [recipe.categoryId ?? recipe.category!, recipe.category!] as const)).entries()), [scopedRecipes]);
  const effectiveCategory = categories.some(([id]) => id === category) ? category : '';
  const visible = useMemo(() => filterViewerRecipes(scopedRecipes, { query, category: effectiveCategory, service, sort, language: i18n.language }),
    [scopedRecipes, query, effectiveCategory, service, sort, i18n.language]);
  const limit = Math.max(60, Number(params.get('limit')) || 60);
  const update = (key: string, value: string) => {
    setParams(previous => { const next = new URLSearchParams(previous); value ? next.set(key, value) : next.delete(key); if (key !== 'limit') next.delete('limit'); return next; }, { replace: true, state: location.state });
  };
  return <section className="rv-page rv-recipes">
    <div className="rv-recipe-heading">
      <h1>{t('recipeViewer.recipeLibrary')}</h1>
      <div className="rv-recipe-filters">
        <label className="rv-search"><Search aria-hidden="true" size={19} /><input className="rv-input" type="search" value={query}
          onChange={event => update('q', event.target.value)} aria-label={t('recipeViewer.searchRecipes')}
          placeholder={t('recipeViewer.searchRecipes')} /></label>
        <label><span className="sr-only">{t('recipeViewer.category')}</span><select className="rv-input" value={effectiveCategory} onChange={event => update('category', event.target.value)}>
          <option value="">{t('recipeViewer.allCategories')}</option>
          {categories.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select></label>
        <label><span className="sr-only">{t('recipeViewer.sort')}</span><select className="rv-input" value={sort} onChange={event => update('sort', event.target.value)}>
          {VIEWER_SORTS.map(value => <option key={value} value={value}>{t(`recipeViewer.sorts.${value}`)}</option>)}
        </select></label>
      </div>
    </div>
    <div className="rv-service-filters" role="group" aria-label={t('recipeViewer.service')}>
      {(['ALL', 'FOOD', 'DRINKS'] as const).filter(value => serviceSupported || value === 'ALL').map(value =>
        <button key={value} className="rv-button" aria-pressed={(serviceSupported ? service : 'ALL') === value}
          onClick={() => update('service', value === 'ALL' ? '' : value)}>{t(`recipeViewer.serviceTypes.${value}`)}</button>)}
      <span className="rv-recipe-count" role="status">{t('recipeViewer.recipeCount', { count: visible.length })}</span>
    </div>
    {visible.length === 0 ? <p className="rv-panel rv-empty">{t(scopedRecipes.length ? 'recipeViewer.noRecipeResults' : 'recipeViewer.noRecipes')}</p> :
      <div className="rv-recipe-grid">{visible.slice(0, limit).map(recipe => {
        const name = recipeName(recipe, i18n.language) || t('recipeViewer.untitledRecipe');
        return <ViewerLink className="rv-recipe-card rv-panel" key={recipe.id} to={`/recipe-viewer/${restaurantId}/recipes/${recipe.id}`}>
          <RecipeImage images={recipe.images} title={name} card />
          <div className="rv-recipe-card-text"><h2>{name}</h2><p className="rv-muted">
            {[recipe.category, t('recipeViewer.componentCount', { count: recipe.components?.length ?? 0 })].filter(Boolean).join(' · ')}
          </p><RecordedAllergens recipe={recipe} /></div>
        </ViewerLink>;
      })}</div>}
    {limit < visible.length && <button className="rv-button rv-load-more" onClick={() => update('limit', String(limit + 60))}>{t('recipeViewer.showMoreRecipes')}</button>}
  </section>;
}

function RecipeImage({ images = [], title, card = false }: { images?: string[]; title: string; card?: boolean }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<string[]>([]);
  const available = images.filter(image => image && !failed.includes(image));
  const active = Math.min(index, Math.max(0, available.length - 1));
  const image = available[active];
  return <div className={`rv-recipe-image${card ? ' rv-card-image' : ''}`}>
    {image ? <img src={image} alt={title} loading={card ? 'lazy' : 'eager'} onError={() => setFailed(previous => [...previous, image])} /> :
      <div className="rv-image-placeholder"><ImageOff aria-hidden="true" size={36} /><span>{t('recipeViewer.noImage')}</span></div>}
    {!card && available.length > 1 && <div className="rv-image-controls">
      <button className="rv-button" onClick={() => setIndex((active + available.length - 1) % available.length)} aria-label={t('recipeViewer.previousImage')}><ChevronLeft aria-hidden="true" /></button>
      <span aria-live="polite">{t('recipeViewer.imagePosition', { current: active + 1, total: available.length })}</span>
      <button className="rv-button" onClick={() => setIndex((active + 1) % available.length)} aria-label={t('recipeViewer.nextImage')}><ChevronRight aria-hidden="true" /></button>
    </div>}
  </div>;
}

function Unavailable({ component = false }: { component?: boolean }) {
  const { restaurantId } = useRecipeViewer();
  const { t } = useTranslation();
  return <section className="rv-page"><ViewerBack fallback={`/recipe-viewer/${restaurantId}/recipes`} />
    <p className="rv-panel rv-empty">{t(component ? 'recipeViewer.componentUnavailable' : 'recipeViewer.recipeUnavailable')}</p></section>;
}

export function RecipeViewerDetail() {
  const { recipeId } = useParams();
  const { recipes, restaurantId } = useRecipeViewer();
  useViewerScroll();
  useViewerScroll('recipeViewerLeft');
  useViewerScroll('recipeViewerRight');
  const recipe = recipes.find(item => item.id === recipeId && item.restaurantId === restaurantId);
  return recipe ? <RecipeDetail key={recipe.id} recipe={recipe} /> : <Unavailable />;
}

function RecipeDetail({ recipe }: { recipe: Recipe }) {
  const { restaurantId, isStale, lastUpdated } = useRecipeViewer();
  const { t, i18n } = useTranslation();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const expanded = params.getAll('expanded');
  const notes = useRecipeNotes(recipe, lastUpdated);
  const title = recipeName(recipe, i18n.language) || t('recipeViewer.untitledRecipe');
  return <section className="rv-page rv-recipes">
    <div className="rv-detail-heading"><ViewerBack fallback={`/recipe-viewer/${restaurantId}/recipes`} /><h1>{title}</h1></div>
    <div className="rv-recipe-detail">
      <div className="rv-recipe-left" id="recipeViewerLeft"><RecipeImage images={recipe.images} title={title} />
        <div className="rv-panel rv-recipe-declaration"><ViewerDeclaration recipe={recipe} stale={isStale} /></div>
      </div>
      <div className="rv-recipe-right" id="recipeViewerRight"><h2>{title}</h2><p className="rv-muted">{recipe.category}</p>
        <div className="rv-component-labels"><h3>{t('recipeViewer.components')}</h3><span>{t('recipeViewer.authoredAmount')}</span></div>
        {!recipe.components?.length && <p className="rv-muted">{t('recipeViewer.noComponents')}</p>}
        {orderedRecipeRows(recipe.components).map((component, index) => {
          const key = componentRouteKey(recipe, component);
          const expansionKey = key ?? `unlinked-${index}`;
          return <details className="rv-panel rv-component" key={expansionKey} open={expanded.includes(expansionKey)} onToggle={event => {
            const open = event.currentTarget.open;
            if (open === expanded.includes(expansionKey)) return;
            setParams(previous => {
              const next = new URLSearchParams(previous);
              const values = next.getAll('expanded').filter(value => value !== expansionKey);
              next.delete('expanded');
              [...values, ...(open ? [expansionKey] : [])].forEach(value => next.append('expanded', value));
              return next;
            }, { replace: true, state: location.state });
          }}>
            <summary><span>{translatedText(component.nameTranslations, i18n.language) || t('recipeViewer.untitledComponent')}</span>
              <span className="rv-muted">{translatedText(component.amountTranslations, i18n.language)}</span></summary>
            <div className="rv-component-expanded"><ComponentContent component={component} compact={!!key} />
              {key && <ViewerLink className="rv-component-link" to={`/recipe-viewer/${restaurantId}/recipes/${recipe.id}/components/${encodeURIComponent(key)}`}>
                {t('recipeViewer.viewComponent')}
              </ViewerLink>}
              <ComponentNotes component={component} notes={key ? notes.items.filter(note => note.componentId === component.id) : []} />
            </div>
          </details>;
        })}
        <NotesStatus notes={notes} />
      </div>
    </div>
  </section>;
}

export function RecipeViewerComponent() {
  const { recipeId, componentKey } = useParams();
  const { recipes, restaurantId } = useRecipeViewer();
  useViewerScroll();
  const recipe = recipes.find(item => item.id === recipeId && item.restaurantId === restaurantId);
  const component = recipe ? resolveViewerComponent(recipe, componentKey) : null;
  return recipe && component ? <ComponentDetail key={`${recipe.id}:${componentKey}`} recipe={recipe} component={component} /> : <Unavailable component />;
}

function ComponentDetail({ recipe, component }: { recipe: Recipe; component: RecipeComponent }) {
  const { restaurantId, lastUpdated } = useRecipeViewer();
  const { t, i18n } = useTranslation();
  const notes = useRecipeNotes(recipe, lastUpdated);
  return <section className="rv-page rv-recipes">
    <div className="rv-detail-heading"><ViewerBack fallback={`/recipe-viewer/${restaurantId}/recipes/${recipe.id}`} />
      <div><p className="rv-muted">{recipeName(recipe, i18n.language)}</p><h1>{translatedText(component.nameTranslations, i18n.language) || t('recipeViewer.untitledComponent')}</h1></div>
    </div>
    <div className="rv-panel rv-component-full"><ComponentContent component={component} />
      <ComponentNotes component={component} notes={notes.items.filter(note => note.componentId === component.id)} />
      <NotesStatus notes={notes} />
    </div>
  </section>;
}

function ComponentContent({ component, compact = false }: { component: RecipeComponent; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const text = (value: unknown) => translatedText(value, i18n.language);
  return <div className={compact ? 'rv-component-content rv-compact' : 'rv-component-content'}>
    {!compact && text(component.amountTranslations) && <p className="rv-muted">{t('recipeViewer.authoredAmount')}: {text(component.amountTranslations)}</p>}
    {!compact && !!component.images?.length && <RecipeImage images={component.images} title={text(component.nameTranslations)} />}
    <div className="rv-ingredient-method"><section><h3>{t('recipeViewer.ingredients')}</h3>
      {component.ingredients?.length ? <ul className="rv-ingredients">{component.ingredients.map((ingredient, index) =>
        <li key={`${ingredient.id}:${index}`}><span>{text(ingredient.nameTranslations)}</span><span className="rv-muted">{text(ingredient.amountTranslations)}</span></li>)}</ul> :
        <p className="rv-muted">{t('recipeViewer.noIngredients')}</p>}
    </section><section><h3>{t('recipeViewer.method')}</h3>
      {component.steps?.length ? <ol className="rv-method">{orderedRecipeRows(component.steps).map((step, index) =>
        <li key={`${step.id}:${index}`}>{text(step.titleTranslations) && <h4>{text(step.titleTranslations)}</h4>}<p>{text(step.descriptionTranslations)}</p></li>)}</ol> :
        <p className="rv-muted">{t('recipeViewer.noMethod')}</p>}
    </section></div>
  </div>;
}

function useRecipeNotes(recipe: Recipe, lastUpdated: number | null) {
  const [items, setItems] = useState<RecipeNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(false);
    fetchNotesByRecipeId(recipe.id).then(result => {
      if (current) setItems(result.filter(note => note.recipeId === recipe.id && note.restaurantId === recipe.restaurantId));
    }).catch(cause => {
      if (current) { console.error('Failed to load recipe viewer notes', cause); setError(true); setItems([]); }
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [recipe.id, recipe.restaurantId, lastUpdated, retry]);
  return { items, loading, error, retry: () => setRetry(value => value + 1) };
}

function NotesStatus({ notes }: { notes: ReturnType<typeof useRecipeNotes> }) {
  const { t } = useTranslation();
  return notes.error ? <div className="rv-notes-status" role="alert"><p>{t('recipeViewer.notesFailed')}</p><button className="rv-button" onClick={notes.retry}>{t('recipeViewer.retryNotes')}</button></div> :
    notes.loading ? <p className="rv-muted" role="status">{t('recipeViewer.notesLoading')}</p> : null;
}

function ComponentNotes({ component, notes }: { component: RecipeComponent; notes: RecipeNote[] }) {
  const { t, i18n } = useTranslation();
  // Authored embedded notes and later API notes have different storage; preserve both without editing.
  const embedded = (component.notes ?? []).filter(note => note.note?.trim());
  if (!embedded.length && !notes.length) return null;
  return <section className="rv-component-notes"><h3>{t('recipeViewer.readOnlyNotes')}</h3>
    {embedded.map((note, index) => <p key={`embedded:${index}`}>{note.note}</p>)}
    {notes.map(note => <article key={note.id}>{Number.isFinite(note.createdAt.getTime()) && <time dateTime={note.createdAt.toISOString()} className="rv-muted">{note.createdAt.toLocaleString(i18n.language)}</time>}<p>{note.content}</p></article>)}
  </section>;
}
